<template>
  <div>
    <br/>
    By <B>year</B>:
    <div class="text-center">
      <svg ref="chart"></svg>
    </div>
    <p v-if="barHovered" class="text-center">
      {{ barHovered.year }}: {{ barHovered.count }} obs.
    </p>
    <p v-else class="text-center">Hover or click bar</p>
  </div>
</template>

<script>
import d3 from '@/assets/d3';

export default {
  props: ['value', 'ufos'],
  data() {
    return {
      width: 320,
      height: 160,
      margin: {
        top: 10,
        right: 10,
        bottom: 20,
        left: 40,
      },
      barHovered: null,
    };
  },
  mounted() {
    this.initChart();
  },
  watch: {
    ufosNested() {
      this.updateChart();
    },
    value() {
      this.updateChart();
    },
  },
  computed: {
    ufosNested() {
      return d3.nest()
        // Group by year
        .key(d => d.properties.year)
        // Count items by year
        .rollup(d => d.length)
        .entries(this.ufosFiltered)
        .map(d => ({ ...d, key: +d.key }))
        .sort((a, b) => a.key - b.key);
    },
    outsideWidth() {
      return this.width + this.margin.left + this.margin.right;
    },
    outsideHeight() {
      return this.height + this.margin.top + this.margin.bottom;
    },
    scale() {
      const from = Math.min(...this.ufosNested.map(d => d.key));
      const to = Math.max(...this.ufosNested.map(d => d.key));
      const domain = [];

      // Create range from year_from to year_to
      for (let index = from; index <= to; index += 1) {
        domain.push(index);
      }

      const x = d3.scaleBand()
        // idem range(from, to)
        .domain(domain)
        .range([0, this.width])
        .padding(0.15);
      const y = d3.scaleLinear()
        .domain([0, Math.max(...this.ufosNested.map(d => d.value))])
        .range([this.height, 0]);
      return { x, y };
    },
    filters() {
      return { ...this.value };
    },
    ufosFiltered() {
      return this.ufos.filter((feature) => {
        let result = true;
        Object.keys(this.filters).forEach((filterParam) => {
          if (filterParam !== 'year') {
            result = result && (this.filters[filterParam].length === 0
            || this.filters[filterParam].includes(feature.properties[filterParam]));
          }
        });
        return result;
      });
    },
  },
  methods: {
    initChart() {
      const svg = d3.select(this.$refs.chart);
      svg
        .attr('width', this.outsideWidth)
        .attr('height', this.outsideHeight);
      const g = svg.append('g')
        .attr('transform', `translate(${this.margin.left},${this.margin.top})`);
      g.append('g')
        .attr('class', 'bars');
      g.append('g')
        .attr('class', 'axis-x')
        .attr('transform', `translate(0, ${this.height})`);
      g.append('g')
        .attr('class', 'axis-y');

      /* g.append('g')
        .attr('class', 'brush')
        .call(d3.brushX().on('brush', this.brushed)); */
    },
    updateChart() {
      const svg = d3.select(this.$refs.chart);
      // DATA JOIN
      const bars = svg.select('.bars')
        .selectAll('rect.bar')
        .data(this.ufosNested, d => d.key);
      // UPDATE
      bars.attr('class', 'bar update');
      // ENTER
      bars
        .enter().append('rect')
        .attr('class', 'bar enter')
        .attr('y', this.height)
        .attr('height', 0)
        // ENTER + UPDATE
        .merge(bars)
        .attr('x', d => this.scale.x(d.key))
        .attr('width', this.scale.x.bandwidth())
        .classed('selected', (d) => {
          const filters = this.value;
          if (filters.year) {
            return filters.year.includes(d.key);
          }
          return null;
        })
        .on('mouseover', (d) => {
          this.barHovered = {
            year: d.key,
            count: d.value,
          };
        })
        .on('mouseout', () => {
          this.barHovered = null;
        })
        .on('click', (d) => {
          const filters = { ...this.value };
          if (filters.year.includes(d.key)) {
            const index = filters.year.indexOf(d.key);
            filters.year.splice(index, 1);
          } else {
            filters.year = [...filters.year, d.key]
              .sort((a, b) => a - b);
          }
          this.$emit('input', filters);
        })
        .transition(500)
        .attr('y', d => this.scale.y(d.value))
        .attr('height', d => this.height - this.scale.y(d.value));
      // EXIT
      bars.exit().remove();

      const xAxis = svg.select('.axis-x');
      const ticksFn = (d) => {
        if (this.ufosNested.length > 16) {
          return (+d % 10 === 0 ? d : null);
        }
        if (this.ufosNested.length > 8) {
          return (+d % 5 === 0 ? d : null);
        }
        return d;
      };
      xAxis.call(d3.axisBottom(this.scale.x)
        .tickFormat(ticksFn));
      const yAxis = svg.select('.axis-y');
      yAxis.call(d3.axisLeft(this.scale.y));
    },
  },
};
</script>

<style scoped>
  svg >>> rect.bar {
    fill: rgb(36, 226, 36);
  }
  svg >>> rect.bar:hover {
    fill: #3388ff;
  }
  svg >>> .selected {
    fill: orange !important;
  }
  svg >>> .selected:hover {
    fill: darkorange !important;
  }
</style>
